export const lightScheme = {
  warning: "#FF8600",
  onWarning: "#FFFFFF",
  warningContainer: "#FFF2E5",
  onWarningContainer: "#8F4C00",
  error: "#F0567A",
  onError: "#FFFFFF",
  errorContainer: "#FDE8ED",
  onErrorContainer: "#5D091D",
  surfaceHighlight: "#F3F4F6",
  surfaceSoft: "#E8E9EA",
  surface: "#FBFCFF",
  surfaceVariant: "#F4F5F7",
  surfaceBack_1: "#D9DDE3",
  surfaceBack_2: "#E8EAEE",
  surfaceInverse: "#282828",
  onSurfaceInverse: "#DFDFDF",
  primary: "#009FDA",
  primaryContainer: "#E9F6FB",
  primaryStrong: "#007099",
  primaryAccent: "#99E3FF",
  primarySelection: "#0EBEFF",
  primaryContainerOutline: "#FFFFFF00",
  onPrimary: "#FFFFFF",
  onPrimaryContainer: "#004B66",
  onPrimaryStrong: "#FFFFFF",
  secondary: "#783EC1",
  secondaryContainer: "#ECE4F6",
  secondaryStrong: "#4D287B",
  secondarySelection: "#760CFC",
  onSecondary: "#FFFFFF",
  onSecondaryContainer: "#4D287B",
  outlineHighlight: "#FFFFFF",
  outlineSuperSoft: "#E9E9E9",
  outlineSoft: "#E0E0E0",
  outline: "#D4D4D4",
  outlineMedium: "#BFBFBF",
  outlineDark: "#6A6A6A",
  outlineInverse: "#4F4F4F",
  generalTextIcon: "#404040",
  generalTextIconVariant: "#A6A6A6",
  generalTextIconFill: "#FFFFFF",
  shadow: "#00000033",
  scrim: "#00000033",
  scrimLight: "#0000000D",
  glow: "#FFFFFF33",
  brandColor: "#004B78",
  onBrandColor: "#FFFFFF",
  neutral: "#7F7F7F",
  neutralContainer: "#7F7F7F",
  onNeutral: "#FFFFFF",
  onNeutralContainer: "#FFFFFF",
  alwaysWhite: "#FFFFFF",
  alwaysBlack: "#000000",
  tertiaryContainer: "#DDECBDB3",
  tertiary: "#7AB800",
  tertiaryStrong: "#588300",
  tag1: "#3F88BE",
  tag2: "#86BBD8",
  tag3: "#F6AE2D",
  tag4: "#F26419",
} as const satisfies LightOrDarkColorScheme;

export const darkScheme = {
  warning: "#FF8600",
  onWarning: "#FFFFFF",
  warningContainer: "#FFF2E5",
  onWarningContainer: "#8F4C00",
  error: "#F0567A",
  onError: "#FFFFFF",
  errorContainer: "#3D3134",
  onErrorContainer: "#F0567A",
  surfaceHighlight: "#FFFFFF12",
  surfaceSoft: "#3F3F3F",
  surface: "#282828",
  surfaceVariant: "#323232",
  surfaceBack_1: "#1D1D1D",
  surfaceBack_2: "#323232",
  surfaceInverse: "#F5F5F5",
  onSurfaceInverse: "#404040",
  primary: "#009FDA",
  primaryContainer: "#FFFFFF0D",
  primaryStrong: "#009FDA",
  primaryAccent: "#99E3FF",
  primarySelection: "#0EBEFF",
  primaryContainerOutline: "#99E3FF33",
  onPrimary: "#FFFFFF",
  onPrimaryContainer: "#009FDA",
  onPrimaryStrong: "#FFFFFF",
  secondary: "#984FF5",
  secondaryContainer: "#984ff5",
  secondaryStrong: "#fff",
  secondarySelection: "#9C4EFF",
  onSecondary: "#FFFFFF",
  onSecondaryContainer: "#B67CFF",
  outlineHighlight: "#5F5F5F",
  outlineSuperSoft: "#4F4F4F",
  outlineSoft: "#3F3F3F",
  outline: "#2D2D2D",
  outlineMedium: "#BFBFBF",
  outlineDark: "#282828",
  outlineInverse: "#CFCFCF",
  generalTextIcon: "#DFDFDF",
  generalTextIconVariant: "#868686",
  generalTextIconFill: "#2D2D2D",
  shadow: "#00000033",
  scrim: "#00000033",
  scrimLight: "#00000033",
  glow: "#FFFFFF0D",
  brandColor: "#009FDA",
  onBrandColor: "#FFFFFF",
  neutral: "#7F7F7F",
  neutralContainer: "#7F7F7F",
  onNeutral: "#FFFFFF",
  onNeutralContainer: "#FFFFFF",
  alwaysWhite: "#FFFFFF",
  alwaysBlack: "#000000",
  tertiaryContainer: "#7AB800",
  tertiary: "#7AB800",
  tertiaryStrong: "#FFFFFF",
  tag1: "#66A1CC",
  tag2: "#AED1E5",
  tag3: "#F8C15E",
  tag4: "#F58348",
} as const satisfies LightOrDarkColorScheme;

export interface LightOrDarkColorScheme {
  readonly warning: "#FF8600";
  readonly onWarning: "#FFFFFF";
  readonly warningContainer: "#FFF2E5";
  readonly onWarningContainer: "#8F4C00";
  readonly error: "#F0567A";
  readonly onError: "#FFFFFF";
  readonly errorContainer: "#FDE8ED" | "#3D3134";
  readonly onErrorContainer: "#5D091D" | "#F0567A";
  readonly surfaceHighlight: "#F3F4F6" | "#FFFFFF12";
  readonly surfaceSoft: "#E8E9EA" | "#3F3F3F";
  readonly surface: "#FBFCFF" | "#282828";
  readonly surfaceVariant: "#F4F5F7" | "#323232";
  readonly surfaceBack_1: "#D9DDE3" | "#1D1D1D";
  readonly surfaceBack_2: "#E8EAEE" | "#323232";
  readonly surfaceInverse: "#282828" | "#F5F5F5";
  readonly onSurfaceInverse: "#DFDFDF" | "#404040";
  readonly primary: "#009FDA";
  readonly primaryContainer: "#E9F6FB" | "#FFFFFF0D";
  readonly primaryStrong: "#007099" | "#009FDA";
  readonly primaryAccent: "#99E3FF";
  readonly primarySelection: "#0EBEFF";
  readonly primaryContainerOutline: "#FFFFFF00" | "#99E3FF33";
  readonly onPrimary: "#FFFFFF";
  readonly onPrimaryContainer: "#004B66" | "#009FDA";
  readonly onPrimaryStrong: "#FFFFFF";
  readonly secondary: "#783EC1" | "#984FF5";
  readonly secondaryContainer: "#ECE4F6" | "#984ff5";
  readonly secondaryStrong: "#4D287B" | "#fff";
  readonly secondarySelection: "#760CFC" | "#9C4EFF";
  readonly onSecondary: "#FFFFFF";
  readonly onSecondaryContainer: "#4D287B" | "#B67CFF";
  readonly outlineHighlight: "#FFFFFF" | "#5F5F5F";
  readonly outlineSuperSoft: "#E9E9E9" | "#4F4F4F";
  readonly outlineSoft: "#E0E0E0" | "#3F3F3F";
  readonly outline: "#D4D4D4" | "#2D2D2D";
  readonly outlineMedium: "#BFBFBF";
  readonly outlineDark: "#6A6A6A" | "#282828";
  readonly outlineInverse: "#4F4F4F" | "#CFCFCF";
  readonly generalTextIcon: "#404040" | "#DFDFDF";
  readonly generalTextIconVariant: "#A6A6A6" | "#868686";
  readonly generalTextIconFill: "#FFFFFF" | "#2D2D2D";
  readonly shadow: "#00000033";
  readonly scrim: "#00000033";
  readonly scrimLight: "#0000000D" | "#00000033";
  readonly glow: "#FFFFFF33" | "#FFFFFF0D";
  readonly brandColor: "#004B78" | "#009FDA";
  readonly onBrandColor: "#FFFFFF";
  readonly neutral: "#7F7F7F";
  readonly neutralContainer: "#7F7F7F";
  readonly onNeutral: "#FFFFFF";
  readonly onNeutralContainer: "#FFFFFF";
  readonly alwaysWhite: "#FFFFFF";
  readonly alwaysBlack: "#000000";
  readonly tertiaryContainer: "#DDECBDB3" | "#7AB800";
  readonly tertiary: "#7AB800";
  readonly tertiaryStrong: "#588300" | "#FFFFFF";
  readonly tag1: "#3F88BE" | "#66A1CC";
  readonly tag2: "#86BBD8" | "#AED1E5";
  readonly tag3: "#F6AE2D" | "#F8C15E";
  readonly tag4: "#F26419" | "#F58348";
}
